import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useT } from '~/i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * La pantalla de recuperación. Función aparte porque la clase no puede usar
 * hooks, y así también cambia de idioma si se toca el selector.
 */
function ErrorFallback() {
  const t = useT('common');
  return (
    <div className="gate">
      <div className="card shadow-lg max-w-md w-full">
        <div className="card-body items-center text-center">
          <img src="/brand/mascot-256.png" alt="" width={64} height={64} className="mb-1" />
          <h2 className="card-title text-xl">{t.errorBoundary.title}</h2>
          <p className="hush text-sm">{t.errorBoundary.body}</p>
          <div className="card-actions mt-4">
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              {t.errorBoundary.reload}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Global error boundary — catches React render crashes (e.g. from browser
 * translation extensions mutating the DOM) and shows a recovery UI instead
 * of a blank page.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}
